import { useCallback, useLayoutEffect, useRef } from 'react';

/**
 * An event handler that keeps its identity while the handler behind it
 * changes, so a memoised row (KPR-1557: ItemCard, the items table's rows)
 * is not re-rendered just because its parent made a new closure.
 *
 *   const onToggle = useStableCallback(props.onToggleSelect);
 *
 * - The wrapper always calls the LATEST handler it was given, so a click, a
 *   timer or an async finish after a re-render acts on what is true now (a
 *   shift range over the order on screen now, not the order a render ago).
 * - It is `undefined` while the handler is not a function, so a child that
 *   asks `typeof onSelect === 'function'` to decide whether it is
 *   interactive still gets the same answer.
 *
 * Only for handlers called from events, timers and effects, never during
 * render, and never for a render prop (renderExpanded): its output has to
 * re-render when it changes.
 */
export default function useStableCallback(handler) {
	const latest = useRef(handler);
	// Swapped in once the render that brought it has committed (React's
	// proposed useEffectEvent), so a render React throws away never leaves its
	// handler behind. Events, timers and effects all run after that.
	useLayoutEffect(() => {
		latest.current = handler;
	});
	const stable = useCallback((...args) => {
		const current = latest.current;
		return typeof current === 'function' ? current(...args) : undefined;
	}, []);
	return typeof handler === 'function' ? stable : undefined;
}
